import { computed } from 'vue';
import { useInfiniteQuery } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import { watch } from 'vue';

export function useAdminAllReviews() {
  const api = useApi();
  const toaster = useToaster();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['admin', 'reviews', 'all'],
    queryFn: ({ pageParam }) => {
      return api.admin.reviewsV2.getAllReviews({
        query: { limit: 20, cursor: pageParam },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined;
    },
    initialPageParam: undefined as string | undefined,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  watch(error, (newError) => {
    if (newError) {
      toaster.create({
        title: 'Failed to load reviews',
        description: newError.message || 'An error occurred while fetching reviews.',
        type: 'error',
      });
      console.error('[useAdminAllReviews] Error:', newError);
    }
  });

  const reviews = computed(() => {
    return data.value?.pages.flatMap((page) => page.data) ?? [];
  });

  const stats = computed(() => {
    return data.value?.pages[0]?.meta;
  });

  return {
    reviews,
    stats,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  };
}

